/** Build-time configured values. */

import { EnvironmentConfig, resolveConfig, type Environments } from "@visioni/a3s-environments";
import z from "zod/v4";
import Constants from "expo-constants";
import { Platform } from "react-native";
import { match } from "ts-pattern";
import { Env, ENVIRONMENT_META_NAMESPACE } from "./env";

/** Build-time configuration passed as Expo config */
export const Cfg = match(Platform.OS)
  .with("ios", () =>
    z
      .object({
        scheme: z.string(),
        ios: z.object({
          bundleIdentifier: z.string(),
        }),
        extra: z.object({ fallbackEnvironmentConfig: EnvironmentConfig, environment: z.string() }),
      })
      .transform((config) => ({
        kind: "ios" as const,
        ...config,
      })),
  )
  .with("android", () =>
    z
      .object({
        scheme: z.string(),
        android: z.object({ package: z.string() }),
        extra: z.object({ fallbackEnvironmentConfig: EnvironmentConfig, environment: z.string() }),
      })
      .transform((config) => ({
        kind: "android" as const,
        ...config,
      })),
  )
  .with("web", () => z.object({}).transform(() => ({ kind: "web" as const })))
  .otherwise(() => {
    throw new Error(`Unsupported platform ${Platform.OS}`);
  });

/** The app's version (`version` in app.config.ts), the same on every platform. */
export const appVersion: string | undefined = Constants.expoConfig?.version;

/** The platform configured for the app at build-time. */
export const platform = Cfg.parse(Constants.expoConfig);

/**
 * The environments configured for the app.
 *
 * Bundle is identical across environments (full Metro cache sharing).
 * Only meta tags in index.html differ between environments.
 */
const fromMeta = (): EnvironmentConfig | null => {
  if (typeof document === "undefined") {
    return null;
  }

  const envVars = Array.from(document.querySelectorAll(`meta[name^="${ENVIRONMENT_META_NAMESPACE}"]`))
    .map((meta) => {
      const name = meta.getAttribute("name");
      const content = meta.getAttribute("content");
      return name && content && name.startsWith(ENVIRONMENT_META_NAMESPACE) ?
          ([name.slice(ENVIRONMENT_META_NAMESPACE.length), content] as const)
        : null;
    })
    .filter((pair): pair is [string, string] => pair !== null)
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {} as Record<string, string>);

  if (Object.keys(envVars).length === 0) {
    return null;
  }

  const result = Env.safeDecode(envVars);
  if (!result.success) {
    console.error("Failed to decode environment from meta tags:", z.treeifyError(result.error));
    return null;
  }

  return result.data.environments ?? null;
};

const fromEnv = (): EnvironmentConfig | null => {
  // Metro statically replaces process.env.EXPO_PUBLIC_ENVIRONMENTS at build time
  // We map it to 'environments' key in the schema
  const result = Env.safeDecode({ environments: process.env.EXPO_PUBLIC_ENVIRONMENTS });
  if (!result.success) {
    console.error("Failed to decode environment from process.env:", z.treeifyError(result.error));
    return null;
  }
  return result.data.environments ?? null;
};

const fromDefault = (): EnvironmentConfig => ({ default: "prd" });

export const environments: Environments = match(platform)
  .with({ kind: "web" }, () => resolveConfig(fromMeta() ?? fromEnv() ?? fromDefault()))
  .with({ kind: "ios" }, (cfg) => resolveConfig(cfg.extra.fallbackEnvironmentConfig ?? fromEnv() ?? fromDefault()))
  .with({ kind: "android" }, (cfg) => resolveConfig(cfg.extra.fallbackEnvironmentConfig ?? fromEnv() ?? fromDefault()))
  .exhaustive();

/**
 * Whether this build is the production release.
 *
 * Native reads the EAS build profile. That is deliberately not the same question as
 * "does it point at Prd": `demo` is a store build served by Prd, but it is kept out
 * of production analytics and keeps its internal debug routes.
 *
 * Web has no build profile — one bundle serves every environment — so it derives from
 * the resolved environment instead. `extra` cannot answer this on web: `APP_ENV` is
 * unset for web exports, so `extra.environment` always reads `"production"` there.
 */
export const isProductionRelease: boolean = match(platform)
  .with({ kind: "web" }, () => environments.default === "prd")
  .with({ kind: "ios" }, { kind: "android" }, (cfg) => cfg.extra.environment === "production")
  .exhaustive();
